(function () {
  const EVENT_NAME = 'Market Back In Stock Requested';
  const KLAVIYO_API_REVISION = '2025-01-15';

  function readJson(el) {
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch (error) {
      return null;
    }
  }

  function variantInput(root) {
    return root.querySelector('form[data-type="add-to-cart-form"] input[name="id"]');
  }

  function isVariantInStock(root, variantId) {
    const map = readJson(root.querySelector('[data-market-stock]')) || {};
    return map[String(variantId)] !== false;
  }

  function amountFromCents(cents) {
    const minor = Number(cents);
    if (!Number.isFinite(minor)) return null;
    return Number((minor / 100).toFixed(2));
  }

  function buildProperties(config, variantId) {
    const variant = (config.variants && config.variants[variantId]) || {};
    return {
      ProductID: config.productId,
      ProductName: config.productName,
      VariantID: Number(variantId),
      VariantName: variant.title || null,
      SKU: variant.sku || null,
      ProductURL: config.productUrl + '?variant=' + variantId,
      ImageURL: config.imageUrl,
      Region: config.region,
      Country: config.country,
      Market: config.market,
      Price: amountFromCents(variant.price),
      Currency: config.currency || null,
      FormattedPrice: variant.formattedPrice || null,
    };
  }

  function sendRequest(config, variantId, profile) {
    const properties = buildProperties(config, variantId);
    const body = {
      data: {
        type: 'event',
        attributes: {
          properties: properties,
          metric: { data: { type: 'metric', attributes: { name: EVENT_NAME } } },
          profile: { data: { type: 'profile', attributes: profile } },
        },
      },
    };

    return klaviyoPost(config, 'events', body);
  }

  function sendBackInStock(config, variantId, email) {
    const body = {
      data: {
        type: 'back-in-stock-subscription',
        attributes: {
          channels: ['EMAIL'],
          profile: {
            data: {
              type: 'profile',
              attributes: { email: email },
            },
          },
        },
        relationships: {
          variant: {
            data: {
              type: 'catalog-variant',
              id: '$shopify:::$default:::' + variantId,
            },
          },
        },
      },
    };

    return klaviyoPost(config, 'back-in-stock-subscriptions', body);
  }

  function sendMarketingOptIn(config, email) {
    if (!config.listId) return Promise.resolve();
    const body = {
      data: {
        type: 'subscription',
        attributes: {
          custom_source: 'Back in stock',
          profile: {
            data: {
              type: 'profile',
              attributes: { email: email },
            },
          },
        },
        relationships: {
          list: {
            data: { type: 'list', id: config.listId },
          },
        },
      },
    };

    return klaviyoPost(config, 'subscriptions', body);
  }

  function klaviyoPost(config, path, body) {
    return fetch(
      'https://a.klaviyo.com/client/' + path + '/?company_id=' + encodeURIComponent(config.publicKey),
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', revision: KLAVIYO_API_REVISION },
        body: JSON.stringify(body),
      }
    ).then((response) => {
      if (!response.ok) throw new Error('Klaviyo ' + path + ' responded ' + response.status);
    });
  }

  function validEmail(value) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
  }

  function init(root) {
    if (!root || root.dataset.marketBisInit === 'true') return;
    const form = root.querySelector('[data-market-bis-form]');
    const buy = root.querySelector('[data-pdp-buy]');
    const config = readJson(root.querySelector('[data-market-bis]'));
    if (!form || !config || !config.publicKey) return;
    root.dataset.marketBisInit = 'true';

    const input = variantInput(root);
    const status = form.querySelector('[data-market-bis-status]');
    const submit = form.querySelector('[type="submit"]');
    const emailInput = form.querySelector('input[type="email"]');
    const submitLabel = submit ? submit.textContent : '';
    const messages = config.messages || {};
    const currentVariantId = () => (input ? input.value : null);

    const resetForm = () => {
      form.classList.remove('is-sent');
      if (status) {
        status.hidden = true;
        status.textContent = '';
        delete status.dataset.state;
      }
      if (submit) {
        submit.disabled = false;
        submit.textContent = submitLabel;
      }
    };

    const plainText = (value) => {
      if (!value || value.indexOf('&') === -1) return value;
      const el = document.createElement('textarea');
      el.innerHTML = value;
      return el.value;
    };

    const showStatus = (message, state) => {
      if (!status) return;
      status.hidden = false;
      status.textContent = plainText(message);
      if (state) status.dataset.state = state;
      else delete status.dataset.state;
    };

    // Shown in place of the sold out button. In-stock variants keep Add to Bag.
    const sync = () => {
      const variantId = currentVariantId();
      const showForm = Boolean(variantId) && !isVariantInStock(root, variantId);
      form.hidden = !showForm;
      if (buy) buy.hidden = showForm;
      if (showForm) return;
      resetForm();
    };

    form.addEventListener('submit', (event) => {
      event.preventDefault();
      const variantId = currentVariantId();
      const email = emailInput ? emailInput.value.trim() : '';
      if (!variantId || !emailInput) return;
      if (!validEmail(email)) {
        showStatus(messages.invalid || 'Enter a valid email address.', 'error');
        emailInput.focus();
        return;
      }

      const marketing = form.querySelector('input[name="marketing"]');
      const wantsMarketing = Boolean(marketing && marketing.checked);
      if (submit) {
        submit.disabled = true;
        submit.textContent = messages.sending || 'Sending...';
      }
      if (status) status.hidden = true;

      const tasks = [
        sendRequest(config, variantId, { email: email }),
        sendBackInStock(config, variantId, email).catch((error) => {
          // Klaviyo rejects a back in stock subscription when Shopify still has
          // stock. The market event above is what records that request.
          const variant = config.variants && config.variants[variantId];
          if (variant && variant.available) return;
          throw error;
        }),
      ];
      if (wantsMarketing) {
        tasks.push(
          sendMarketingOptIn(config, email).catch((error) => {
            console.error('[market-back-in-stock] Could not subscribe to the list', error);
          })
        );
      }

      Promise.all(tasks)
        .then(() => {
          form.classList.add('is-sent');
          showStatus(messages.success || "You're on the list. We'll email you when this item is back in stock.");
        })
        .catch((error) => {
          console.error('[market-back-in-stock] Could not record request', error);
          if (submit) {
            submit.disabled = false;
            submit.textContent = submitLabel;
          }
          showStatus(messages.error || 'We couldn\'t save that just now. Please try again.', 'error');
        });
    });

    if (input) input.addEventListener('change', sync);
    sync();
  }

  function initAll() {
    document.querySelectorAll('.pdp-editorial[data-section-id]').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => {
    const root = event.target.querySelector
      ? event.target.querySelector('.pdp-editorial[data-section-id]')
      : null;
    if (root) {
      delete root.dataset.marketBisInit;
      init(root);
    }
  });
})();
